import React, { useEffect, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_ORDER_ROUTER } from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

/** Khớp `CANCEL_DEPOSITED_CASH_RESULT` bên accounting. */
const RESULT_OK = 1;
const RESULT_BLOCKED = 2;

interface Preview {
  result: number;
  can_cancel: boolean;
  message: string;
  driver_id: number;
  driver_name: string;
  amount: number;
  collection_already_cancelled: boolean;
  deposited_at: string;
  withdrawal_ids: number[];
  receipt_ids: number[];
}

interface Props {
  orderId: number;
  orderCode?: string;
  onClose: () => void;
  onSaved: () => void;
}

const fmt = (v: number) => `${Number(v || 0).toLocaleString("vi-VN")}đ`;
const fmtDateTime = (s: string) => {
  if (!s) return "—";
  const d = new Date(s.replace(" ", "T"));
  return isNaN(d.getTime()) ? s : d.toLocaleString("vi-VN", { hour12: false });
};

/**
 * "Huỷ SQTM" (`GET/POST :id/cash-deposit-cancel`, quyền DRIVER_CASH_DEPOSIT_CANCEL). Dùng khi đơn tiền mặt đã
 * hoàn tất mà tài xế ĐÃ NỘP sổ quỹ nên không đổi được hình thức thanh toán. Mở modal → xem trước (dry-run) những
 * gì sẽ huỷ; xác nhận → huỷ khoản thu, lần nộp và phiếu thu nộp quỹ. KHÔNG tự đổi hình thức — sau đó bấm "Đổi HTTT".
 */
const CancelCashDepositModal: React.FC<Props> = ({ orderId, orderCode, onClose, onSaved }) => {
  const { request: get } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: post } = useFetchData.Post<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await get({ endPoint: API_ORDER_ROUTER.API_CASH_DEPOSIT_CANCEL(orderId) });
        if (!mounted) return;
        if (res && isSuccess(res)) setPreview(res.data as Preview);
        else setLoadError(res?.message || "Không tải được sổ quỹ tiền mặt của đơn");
      } catch (e: any) {
        if (mounted) setLoadError(e?.message || "Không tải được sổ quỹ tiền mặt của đơn");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const save = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    try {
      const res = await post({
        endPoint: API_ORDER_ROUTER.API_CASH_DEPOSIT_CANCEL(orderId),
        data: { reason: reason.trim() },
      });
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message || "Huỷ sổ quỹ tiền mặt thất bại", type: "error" });
        return;
      }
      showToast({ message: "Đã huỷ sổ quỹ tiền mặt. Giờ có thể đổi hình thức thanh toán của đơn.", type: "success" });
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || "Huỷ sổ quỹ tiền mặt thất bại", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const canCancel = !!preview?.can_cancel;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[520px] max-w-[95vw] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Huỷ sổ quỹ tiền mặt (SQTM)</h3>
          <p className="text-xs text-slate-500 mt-1">
            Đơn {orderCode ? <b>{orderCode}</b> : `#${orderId}`}
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {loading && <div className="text-sm text-slate-500">Đang kiểm tra sổ quỹ...</div>}

          {!loading && loadError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{loadError}</div>
          )}

          {!loading && preview && !canCancel && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                preview.result === RESULT_BLOCKED
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-slate-50 border-slate-200 text-slate-700"
              }`}
            >
              {preview.message}
              {preview.result === RESULT_BLOCKED && " — vui lòng liên hệ kế toán."}
            </div>
          )}

          {!loading && preview && preview.result === RESULT_OK && (
            <>
              <div className="rounded-lg border border-slate-200 divide-y text-sm">
                <div className="flex justify-between px-3 py-2">
                  <span className="text-slate-500">Nhân viên nộp</span>
                  <span className="font-medium text-slate-800">
                    {preview.driver_name || `#${preview.driver_id}`}
                  </span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-slate-500">Số tiền</span>
                  <span className="font-semibold text-slate-800">{fmt(preview.amount)}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-slate-500">Thời điểm nộp</span>
                  <span className="text-slate-800">{fmtDateTime(preview.deposited_at)}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-slate-500">Khoản thu của đơn</span>
                  <span className="text-slate-800">
                    {preview.collection_already_cancelled ? "Đã huỷ trước đó" : "Đang hiệu lực → sẽ huỷ"}
                  </span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-slate-500">Lần nộp / phiếu thu sẽ huỷ</span>
                  <span className="text-slate-800">
                    {preview.withdrawal_ids.map((id) => `#${id}`).join(", ") || "—"}
                    {" / "}
                    {preview.receipt_ids.map((id) => `#${id}`).join(", ") || "—"}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-1">
                <div>
                  Khoản thu, lần nộp và phiếu thu nộp quỹ {fmt(preview.amount)} sẽ bị huỷ — sổ quỹ công ty giảm{" "}
                  {fmt(preview.amount)}. Chỉ làm khi tiền này thực tế không thuộc đơn tiền mặt (vd khách tính công nợ).
                </div>
                {preview.collection_already_cancelled ? (
                  <div>Khoản thu đã huỷ từ trước nên số dư của nhân viên sẽ được cộng lại {fmt(preview.amount)}.</div>
                ) : (
                  <div>Số dư của nhân viên không đổi (huỷ cả khoản thu lẫn khoản nộp).</div>
                )}
                <div>
                  Thao tác này <b>không</b> đổi hình thức thanh toán — sau khi huỷ, bấm "Đổi HTTT" để chuyển đơn sang
                  công nợ.
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Lý do <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-300 focus:outline-none resize-none"
                  rows={2}
                  maxLength={255}
                  placeholder="VD: Khách tính công nợ, nhân viên bấm tiền mặt rồi nộp quỹ..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
          >
            Trở lại
          </button>
          {canCancel && (
            <button
              onClick={save}
              disabled={saving || !reason.trim()}
              className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
            >
              {saving ? "Đang huỷ..." : "Xác nhận huỷ SQTM"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelCashDepositModal;
